import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import useAuth from './useAuth';
import { profileCache } from '@/lib/profileCache';
const LOCAL_FLAG_KEY = 'giftmap_profile_complete';
const DEBUG = true; // flip to false to silence logs


export default function useProfileComplete() {
  const { user } = useAuth();
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!user) {
      setIsComplete(false);
      setLoading(false);
      return;
    }

    const checkProfile = async () => {
      setLoading(true);
      try {
        // 0) LocalStorage optimistic flag to avoid redirect on app relaunch
        try {
          const localFlag = localStorage.getItem(LOCAL_FLAG_KEY) === 'true';
          if (localFlag && !isComplete) {
            if (DEBUG) console.log('[useProfileComplete] localStorage flag true, optimistic complete');
            setIsComplete(true);
          }
        } catch {}

        // 1) Try cache first
        const cached = profileCache.get(user.id);
        if (cached && cached.username) {
          if (DEBUG) console.log('[useProfileComplete] cache hit');
          setIsComplete(true);
          try { localStorage.setItem(LOCAL_FLAG_KEY, 'true'); } catch {}
          setLoading(false);
          return;
        }
        
        // 2) Fetch from database
        if (DEBUG) console.log('[useProfileComplete] fetching from DB');
        const { data, error } = await supabase
          .from('users')
          .select('username, name, avatar_url')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[useProfileComplete] DB error:', error);
          // Do not override to false if we already think it's complete
          // Leave isComplete as-is to avoid unwanted redirect on transient failures
        } else {
          const complete = !!data?.username;
          setIsComplete(complete);
          
          // Cache the profile data
          if (complete) {
            profileCache.set({
              id: user.id,
              username: data.username,
              name: data.name,
              avatar_url: data.avatar_url,
            });
            try { localStorage.setItem(LOCAL_FLAG_KEY, 'true'); } catch {}
          } else {
            try { localStorage.removeItem(LOCAL_FLAG_KEY); } catch {}
          }
        }
      } catch (error) {
        console.error('[useProfileComplete] unexpected error:', error);
        // Preserve current isComplete state
      } finally {
        setLoading(false);
      }
    };

    checkProfile();
  }, [user, refetchTrigger]);

  const refetch = async () => {
    if (!user) {
      setIsComplete(false);
      return false;
    }

    setLoading(true);
    try {
      // Clear cache and fetch fresh data
      profileCache.clear();
      
      const { data, error } = await supabase
        .from('users')
        .select('username, name, avatar_url')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Profile check error:', error);
        setIsComplete(false);
        try { localStorage.removeItem(LOCAL_FLAG_KEY); } catch {}
        return false;
      } else {
        const complete = !!data?.username;
        setIsComplete(complete);
        
        // Cache the profile data
        if (complete) {
          profileCache.set({
            id: user.id,
            username: data.username,
            name: data.name,
            avatar_url: data.avatar_url,
          });
          try { localStorage.setItem(LOCAL_FLAG_KEY, 'true'); } catch {}
        } else {
          try { localStorage.removeItem(LOCAL_FLAG_KEY); } catch {}
        }
        
        return complete;
      }
    } catch (error) {
      console.error('Profile check error:', error);
      setIsComplete(false);
      try { localStorage.removeItem(LOCAL_FLAG_KEY); } catch {}
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { isComplete, loading, refetch };
}
